import { noop } from '@babel/types'
import { useTranslation } from 'next-i18next'
import { useCallback, useRef, useState } from 'react'

import { LOCALE_COOKIE, TAbsoluteLocale, TCountry } from '@src/config/i18n.config'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getCookieStorage } from '@src/utils/enhancedCookie'
import { initializeFeCommon } from '@src/utils/feCommonInitialize'
import { setLocalisationDataInStorage } from '@src/utils/general.utils'
import { TRegionDetail, updateCommunicationPreference } from '@src/utils/i18n.utils'

interface TUseLocaleContextHandlers {
  regionDetails: TRegionDetail
  setLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  setIsLocaleConfirmationModalOpen: (isLocaleConfirmationModalOpen: boolean) => void
  setConfirmationLocale: (confirmationLocale: TAbsoluteLocale | TCountry | null) => void
}

const useLocaleContextHandlers = ({
  setIsLocaleConfirmationModalOpen,
  setConfirmationLocale,
  regionDetails,
  setLocale,
}: TUseLocaleContextHandlers) => {
  const { i18n } = useTranslation()
  const [onConfirm, setOnConfirm] = useState<() => void>(noop)
  const communicationPreferenceAbortController = useRef<AbortController | null>(null)
  const handleSetLocale = useCallback(
    async (locale: TAbsoluteLocale, clientSide = false, shouldUpdateCommunicationPreference = true) => {
      const loggedIn = AuthUtil.isLoggedIn()
      const updateLocaleSettings = async () => {
        setLocalisationDataInStorage(locale, regionDetails)
        getCookieStorage().setCookieLocalized(LOCALE_COOKIE, locale, { isGlobal: true })
        await i18n.changeLanguage(locale)
        await setLocale(locale)
        initializeFeCommon()
      }
      if (shouldUpdateCommunicationPreference && loggedIn) {
        communicationPreferenceAbortController.current?.abort()
        communicationPreferenceAbortController.current = new AbortController()

        try {
          const response = await updateCommunicationPreference({
            locale,
            regionDetails,
            abortController: communicationPreferenceAbortController.current,
          })
          if (response?.status === 200) {
            await updateLocaleSettings()
          }
        } catch (error) {
          if (communicationPreferenceAbortController.current.signal.aborted) return
          console.error('Failed to update preferred language and communication preference: ', error)
        }
      } else {
        await updateLocaleSettings()
      }
      if (!clientSide) window.location.reload()
    },
    [i18n, regionDetails, setLocale],
  )

  const closeConfirmationModal = useCallback(
    (confirm: boolean) => {
      if (confirm) onConfirm()
      setIsLocaleConfirmationModalOpen(false)
      setOnConfirm(noop)
      setConfirmationLocale(null)
    },
    [setIsLocaleConfirmationModalOpen, setOnConfirm, setConfirmationLocale, onConfirm],
  )

  const showConfirmationModal = useCallback(
    (confirmationLocale: TAbsoluteLocale, callback: () => void) => {
      setConfirmationLocale(confirmationLocale)
      setOnConfirm(() => callback)
      setIsLocaleConfirmationModalOpen(true)
    },
    [setIsLocaleConfirmationModalOpen, setConfirmationLocale],
  )

  return {
    handleSetLocale,
    closeConfirmationModal,
    showConfirmationModal,
    onConfirm,
  }
}

export default useLocaleContextHandlers
